import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  AccountBalanceWallet,
  Group,
  ReceiptLong,
  Timeline,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

const BRAND = {
  dark: "#00619E",
  primary: "#0080AA",
  light: "#00A8E8",
  bg: "#F2FAFE",
  border: "#CFEAF6",
};

const currencyBRL = (value) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  }).format(Number(value || 0));

const numberBRL = (value) =>
  new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const shortMonth = (ano, mes) => {
  if (!ano || !mes) return "--";
  const meses = ["JAN", "FEV", "MAR", "ABR", "MAI", "JUN", "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"];
  const mesLabel = meses[mes - 1];
  if (!mesLabel) return "--";
  const ano2 = String(ano).slice(-2);
  return `${mesLabel}/${ano2}`;
};

const startOfDay = (dateValue) => {
  const d = new Date(dateValue);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getUsuarioNome = () => {
  try {
    const raw = localStorage.getItem("usuario");
    if (!raw) return "Usuario";
    const parsed = JSON.parse(raw);
    return parsed?.nome || parsed?.Nome || "Usuario";
  } catch {
    return "Usuario";
  }
};

const normalizeArray = (resp) => {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp?.data?.items)) return resp.data.items;
  return [];
};

const normalizeHistorico = (resp) => {
  const data = resp?.data ?? resp;
  if (!data || typeof data !== "object") {
    return { itens: [], valorEmAberto: 0 };
  }

  const itens = Array.isArray(data.itens) ? data.itens : Array.isArray(data.Itens) ? data.Itens : [];
  const valorEmAberto = Number(data.valorEmAberto ?? data.ValorEmAberto ?? 0);
  return { itens, valorEmAberto };
};

const normalizeObs = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase();

export default function Dashboard() {
  const navigate = useNavigate();

  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [boletos, setBoletos] = useState([]);
  const [comissoes, setComissoes] = useState([]);
  const [comissaoHistorico, setComissaoHistorico] = useState([]);
  const comissaoScrollRef = useRef(null);

  useEffect(() => {
    const admRaw = localStorage.getItem("administrador");
    if (!admRaw) {
      setIsAdmin(false);
      return;
    }

    try {
      const parsed = JSON.parse(admRaw);
      setIsAdmin(parsed === true || parsed === "S");
    } catch {
      setIsAdmin(admRaw === "true" || admRaw === "S");
    }
  }, []);

  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      setError("");

      const vendedorRaw = (localStorage.getItem("codigoVendedor") || "").replace(/^"|"$/g, "");
      const requests = [api.get("/financeiro/boletos-abertos")];
      const hasSeller = vendedorRaw && vendedorRaw !== "0";
      let comissoesIndex = -1;
      let historicoIndex = -1;

      if (hasSeller) {
        comissoesIndex = requests.push(api.get(`/comissoes/${vendedorRaw}`)) - 1;
        historicoIndex = requests.push(api.get(`/comissoes/${vendedorRaw}/historico-mensal?meses=12`)) - 1;
      }

      const results = await Promise.allSettled(requests);
      const boletosResult = results[0];
      const comissoesResult = comissoesIndex >= 0 ? results[comissoesIndex] : null;
      const historicoResult = historicoIndex >= 0 ? results[historicoIndex] : null;

      if (boletosResult?.status === "fulfilled") {
        setBoletos(normalizeArray(boletosResult.value));
      } else {
        setBoletos([]);
      }

      if (hasSeller && comissoesResult?.status === "fulfilled") {
        setComissoes(normalizeArray(comissoesResult.value));
      } else {
        setComissoes([]);
      }

      if (hasSeller && historicoResult?.status === "fulfilled") {
        const historicoNorm = normalizeHistorico(historicoResult.value);
        setComissaoHistorico(historicoNorm.itens);
      } else {
        setComissaoHistorico([]);
      }

      const allFailed =
        boletosResult?.status === "rejected" &&
        (!hasSeller || (comissoesResult?.status === "rejected" && historicoResult?.status === "rejected"));

      if (allFailed) {
        setError("Nao foi possivel carregar os dados do dashboard.");
      }

      setLoading(false);
    };

    loadDashboardData();
  }, []);

  const usuarioNome = getUsuarioNome();

  const metrics = useMemo(() => {
    const today = startOfDay(new Date());

    const boletosNorm = boletos.map((b) => ({
      valor: Number(b.valorParcela || b.valor || 0),
      vencimento: startOfDay(b.vencimento),
    }));

    const boletosVencidosValor = boletosNorm
      .filter((b) => b.vencimento < today)
      .reduce((sum, b) => sum + b.valor, 0);

    const comissaoEmAberto = comissoes.reduce((sum, c) => {
      const obs = normalizeObs(c.observacao);
      const valor = Number(c.valorTotal || 0);
      if (obs === "VENDA DE MERCADORIA") return sum + valor;
      if (obs === "VALE - VENDEDORES") return sum - valor;
      if (obs === "DEVOLUCAO DE VENDA") return sum - valor;
      if (obs === "ESTORNO - CANCELAMENTO PEDIDO") return sum - valor;
      return sum;
    }, 0);

    const comissao12Meses = comissaoHistorico
      .map((item) => ({
        ano: Number(item.ano ?? item.Ano ?? 0),
        mes: Number(item.mes ?? item.Mes ?? 0),
        valor: Number(item.valorTotal ?? item.valor ?? 0),
      }))
      .filter((item) => item.ano > 0 && item.mes >= 1 && item.mes <= 12)
      .map((item) => ({
        ...item,
        label: shortMonth(item.ano, item.mes),
      }))
      .slice(-12);

    const comissaoGrafico = [
      ...comissao12Meses,
      { label: "EM ABERTO", valor: Number(comissaoEmAberto || 0), emAberto: true },
    ];

    const maxComissao12 = Math.max(...comissaoGrafico.map((item) => Math.abs(item.valor)), 1);
    const totalComissao12 = comissao12Meses.reduce((sum, item) => sum + item.valor, 0);

    return {
      boletosVencidosValor,
      comissao12Meses,
      comissaoGrafico,
      maxComissao12,
      totalComissao12,
      comissaoEmAberto,
    };
  }, [boletos, comissaoHistorico, comissoes]);

  useEffect(() => {
    if (loading || metrics.comissaoGrafico.length === 0) return;

    const el = comissaoScrollRef.current;
    if (!el) return;

    const scrollToEnd = () => {
      el.scrollLeft = el.scrollWidth;
    };

    scrollToEnd();
    const timer = setTimeout(scrollToEnd, 120);
    window.addEventListener("resize", scrollToEnd);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", scrollToEnd);
    };
  }, [loading, metrics.comissaoGrafico.length]);
  const cardSx = {
    borderRadius: 4,
    border: `1px solid ${BRAND.border}`,
    background: "#FFFFFF",
    boxShadow: "0 10px 30px rgba(0, 128, 170, 0.08)",
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 6 }}>
      <Paper
        sx={{
          p: { xs: 3, md: 4 },
          mb: 3,
          borderRadius: 4,
          color: "#fff",
          position: "relative",
          overflow: "hidden",
          background: `linear-gradient(135deg, ${BRAND.dark} 0%, ${BRAND.primary} 45%, ${BRAND.light} 100%)`,
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            opacity: 0.16,
            backgroundImage:
              "radial-gradient(circle at 20% 25%, #ffffff 0 2px, transparent 3px), radial-gradient(circle at 80% 70%, #ffffff 0 2px, transparent 3px)",
            backgroundSize: "90px 90px",
          },
        }}
      >
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Typography variant="h4" fontWeight={700}>
            Dashboard Comercial
          </Typography>
          <Typography variant="body1" sx={{ mt: 0.5, opacity: 0.92 }}>
            Ola, {usuarioNome}. Visao geral de pedidos, financeiro e comissoes.
          </Typography>
        </Box>
      </Paper>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ py: 10, textAlign: "center" }}>
          <CircularProgress sx={{ color: BRAND.primary }} />
        </Box>
      ) : (
        <>
          <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Paper sx={{ ...cardSx, p: 3, background: BRAND.bg }}>
                <Typography variant="h6" fontWeight={700}>
                  Acesso rapido
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                  Navegacao principal do ERP
                </Typography>

                <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap>
                  <Button variant="contained" onClick={() => navigate("/pedidos")} startIcon={<ReceiptLong />}>
                    Pedidos
                  </Button>
                  <Button variant="outlined" onClick={() => navigate("/financeiro")}>
                    Financeiro
                  </Button>
                  <Button variant="outlined" onClick={() => navigate("/comissao")}>
                    Comissao
                  </Button>
                  <Button variant="outlined" onClick={() => navigate("/tabela-preco")}>
                    Tabela de Preco
                  </Button>
                  {isAdmin && (
                    <Button variant="outlined" onClick={() => navigate("/usuarios")} startIcon={<Group />}>
                      Usuarios
                    </Button>
                  )}
                  {isAdmin && (
                    <Button variant="outlined" onClick={() => navigate("/mapa-carga")}>Mapa de Carga</Button>
                  )}
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ ...cardSx, p: 2.5 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <AccountBalanceWallet sx={{ color: BRAND.primary }} />
                  <Typography color="text.secondary">Boletos em aberto</Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  Titulos vencidos
                </Typography>
                <Typography variant="h4" fontWeight={800} sx={{ mt: 0.3, color: "#D32F2F" }}>
                  {currencyBRL(metrics.boletosVencidosValor)}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ ...cardSx, p: 3 }}>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1}>
                  <Box>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <Timeline sx={{ color: BRAND.primary }} />
                      <Typography variant="h6" fontWeight={700}>
                        Comissao mensal (ultimos 12 periodos)
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.7 }}>
                      O grafico mostra somente os fechamentos por mes em DATA_PAGAMENTO.
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" align="right">
                      Total fechado (12 meses)
                    </Typography>
                    <Typography variant="h6" fontWeight={700} align="right">
                      {currencyBRL(metrics.totalComissao12)}
                    </Typography>
                  </Box>
                </Stack>

                {metrics.comissao12Meses.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                    Sem dados de comissao para os ultimos 12 periodos.
                  </Typography>
                ) : (
                  <>
                    <Box
                      ref={comissaoScrollRef}
                      sx={{
                        mt: 3,
                        overflowX: "auto",
                        overflowY: "hidden",
                        pb: 0.5,
                        "&::-webkit-scrollbar": { height: 8 },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#BFDDEA",
                          borderRadius: 10,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: `${metrics.comissaoGrafico.length * 92}px`,
                          display: "flex",
                          alignItems: "flex-end",
                          justifyContent: "flex-start",
                          gap: 1.5,
                          height: 260,
                          borderBottom: `1px solid ${BRAND.border}`,
                          pb: 1,
                        }}
                      >
                        {metrics.comissaoGrafico.map((item) => {
                          const h = Math.max(
                            (Math.abs(item.valor) / metrics.maxComissao12) * 150,
                            item.valor !== 0 ? 12 : 6
                          );

                          const corBarra =
                            item.emAberto
                              ? "linear-gradient(180deg, #FFC078 0%, #F08C00 100%)"
                              : item.valor < 0
                              ? "linear-gradient(180deg, #FFB3B8 0%, #EA5A63 100%)"
                              : `linear-gradient(180deg, ${BRAND.light} 0%, ${BRAND.primary} 100%)`;

                          return (
                            <Box
                              key={item.emAberto ? "em-aberto" : `${item.ano}-${item.mes}`}
                              sx={{
                                width: 76,
                                flex: "0 0 auto",
                                minWidth: 76,
                                textAlign: "center",
                              }}
                              title={`${item.label}: ${currencyBRL(item.valor)}`}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: item.emAberto ? "#8A4B00" : item.valor < 0 ? "#A61E2D" : BRAND.dark,
                                  fontWeight: 700,
                                  display: "block",
                                  minHeight: 18,
                                  px: 0.5,
                                  lineHeight: 1.2,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {numberBRL(item.valor)}
                              </Typography>
                              <Box
                                sx={{
                                  mt: 0.8,
                                  borderRadius: "10px 10px 4px 4px",
                                  height: h,
                                  background: corBarra,
                                }}
                              />
                              <Typography
                                variant="caption"
                                color={item.emAberto ? "#8A4B00" : "text.secondary"}
                                sx={{ mt: 0.8, display: "block", fontWeight: item.emAberto ? 700 : 500 }}
                              >
                                {item.label}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Comissao em aberto (proximo fechamento):
                      </Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ color: "#8A4B00" }}>
                        {currencyBRL(metrics.comissaoEmAberto)}
                      </Typography>
                    </Stack>
                  </>
                )}
              </Paper>
            </Grid>

          </Grid>
        </>
      )}
    </Container>
  );
}






